import type { Dimensions } from "../canvas/canvasTypes";

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10] as const;

export interface DecodedArtwork {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

export function readPngDimensions(bytes: Uint8Array): Dimensions {
  if (bytes.length < 24 || PNG_SIGNATURE.some((byte, index) => bytes[index] !== byte)) {
    throw new Error("The artwork is not a valid PNG file.");
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const width = view.getUint32(16);
  const height = view.getUint32(20);
  if (width === 0 || height === 0) throw new Error("The PNG dimensions are invalid.");
  return { width, height };
}

export async function inspectPngBlob(blob: Blob): Promise<Dimensions> {
  if (blob.size === 0 || (blob.type && blob.type !== "image/png")) {
    throw new Error("The artwork Blob is not a PNG.");
  }
  return readPngDimensions(new Uint8Array(await blob.slice(0, 24).arrayBuffer()));
}

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: "image/png" | "image/webp" = "image/png",
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("The browser could not encode the artwork."));
          return;
        }
        if (blob.size === 0 || !blob.type.startsWith("image/")) {
          reject(new Error("The browser returned an invalid encoded image."));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}

export async function decodeArtworkBlob(
  blob: Blob,
  expectedDimensions: Dimensions,
): Promise<DecodedArtwork> {
  const encodedDimensions = await inspectPngBlob(blob);
  if (
    encodedDimensions.width !== expectedDimensions.width ||
    encodedDimensions.height !== expectedDimensions.height
  ) {
    throw new Error("The saved artwork dimensions do not match the project.");
  }

  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(blob);
    if (bitmap.width !== expectedDimensions.width || bitmap.height !== expectedDimensions.height) {
      bitmap.close();
      throw new Error("The decoded artwork dimensions do not match the project.");
    }
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      release: () => bitmap.close(),
    };
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.decoding = "async";
    image.src = objectUrl;
    await image.decode();
    if (
      image.naturalWidth !== expectedDimensions.width ||
      image.naturalHeight !== expectedDimensions.height
    ) {
      throw new Error("The decoded artwork dimensions do not match the project.");
    }
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
}
